/* eslint-disable @typescript-eslint/prefer-optional-chain */
import { t } from '@if/core/i18n'
import { EFinancingProgram, EPlanType, PlanPromoRule } from '@if/core/types'
import { normalizeDateTime } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { Add, Delete, Edit } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Card, CardActions, CardContent, Chip, Collapse, Grid, IconButton, Typography } from '@mui/material'
import { FormatPromoCode } from '@src/containers/App/utils/Formatter'
import PlanPromoRuleDialog from '@src/containers/EditMerchant/components/PlanPromoRuleDialog'
import { ApplicableMerchantPaymentPlan } from '@src/data/types/ApplicableMerchantPaymentPlan'
import { isEqual } from 'lodash-es'
import { useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import ExpandMore from '../../../components/ExpandMore'
import SelectedPromoPlanDisplay from './SelectedPromoPlanDisplay'

export const MerchantPlanPromoRulesSchema = yup.object().shape({
  planPromoRules: yup
    .array()
    .of(
      yup.object().shape({
        defaultPromoCode: yup.string().required(),
        financingProgramId: yup.string().required(),
        planName: yup.string().oneOf(Object.values(EPlanType)).required(),
        selectablePromoCodes: yup.array().of(yup.string().required()).default([]),
      }),
    )
    .default([]),
})
export type MerchantPlanPromoRulesSchemaType = yup.InferSubmit<typeof MerchantPlanPromoRulesSchema>

interface Props {
  availablePaymentPlans: ApplicableMerchantPaymentPlan[]
  initialPlanPromoRules: PlanPromoRule[]
  planPromoRules: PlanPromoRule[]
  supportFinancingPrograms?: EFinancingProgram[]
}

const MerchantPromoPlanPanel = ({
  availablePaymentPlans,
  initialPlanPromoRules,
  planPromoRules,
  supportFinancingPrograms,
}: Props) => {
  const { control } = useFormContext<MerchantPlanPromoRulesSchemaType>()
  const {
    field: { onChange, value },
  } = useController({
    control,
    name: 'planPromoRules',
  })

  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({})

  const [openPlanPromoRuleDialog, setOpenPlanPromoRuleDialog] = useState(false)

  const [promoRules, setPromoRules] = useState<PlanPromoRule[]>(planPromoRules)
  const [entry, setEntry] = useState<PlanPromoRule>()

  const getBestMatchingPlan = (promoCode: string): ApplicableMerchantPaymentPlan | undefined => {
    if (!promoCode) return undefined

    const matchingPlans = availablePaymentPlans.filter((item) => item.promoCode === promoCode)
    if (matchingPlans.length === 0) return undefined
    if (matchingPlans.length === 1) return matchingPlans[0]

    const today = new Date()
    return [...matchingPlans].sort((a, b) => {
      const aExpired = a.expiresOn && today > new Date(a.expiresOn)
      const bExpired = b.expiresOn && today > new Date(b.expiresOn)

      if (aExpired && !bExpired) return 1
      if (!aExpired && bExpired) return -1

      if (a.expiresOn && b.expiresOn) {
        return new Date(b.expiresOn).getTime() - new Date(a.expiresOn).getTime()
      }

      if (!a.expiresOn) return -1
      if (!b.expiresOn) return 1

      return 0
    })[0]
  }

  const isPromoCodeExpired = (entry: PlanPromoRule): boolean => {
    const paymentPlan = getBestMatchingPlan(entry.defaultPromoCode)
    if (!paymentPlan || !paymentPlan.expiresOn) return false

    return new Date() > new Date(paymentPlan.expiresOn)
  }

  const isPromoCodeNotActivated = (entry: PlanPromoRule): boolean => {
    const paymentPlan = getBestMatchingPlan(entry.defaultPromoCode)
    if (!paymentPlan || !paymentPlan.activeFrom) return false

    return normalizeDateTime(new Date()) < normalizeDateTime(new Date(paymentPlan.activeFrom))
  }

  const handleExpandClick = (index: number) => {
    setExpandedCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const deleteEntry = (entry: PlanPromoRule) => {
    setPromoRules((prev) => prev.filter((item) => item !== entry))
    const updated = value.filter(
      (item) =>
        !(
          item.defaultPromoCode === entry.defaultPromoCode &&
          item.financingProgramId === entry.financingProgramId &&
          item.planName === entry.planName
        ),
    )
    onChange(updated)
  }

  const editEntry = (entry: PlanPromoRule) => {
    setEntry(entry)
    setOpenPlanPromoRuleDialog(true)
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('promoCodePanel.defaultPromos')}
      </Typography>
      {initialPlanPromoRules.length === 0 && <Typography sx={{ mb: 2 }}>{t('promoCodePanel.noPromoCode')}</Typography>}
      <Grid container spacing={2}>
        {promoRules.map((entry, index) => (
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
            key={index}
          >
            <Card
              variant="outlined"
              sx={{
                borderColor: initialPlanPromoRules.some(
                  (e) =>
                    e.defaultPromoCode === entry.defaultPromoCode &&
                    e.financingProgramId === entry.financingProgramId &&
                    e.planName === entry.planName &&
                    isEqual(e.selectablePromoCodes, entry.selectablePromoCodes),
                )
                  ? undefined
                  : 'yellow',
              }}
            >
              <CardContent>
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{ position: 'relative' }}>
                    <IconButton
                      onClick={() => editEntry(entry)}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 32,
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={() => deleteEntry(entry)}
                      color="error"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>

                  {isPromoCodeExpired(entry) && (
                    <Box sx={{ mb: 2, mt: 4 }}>
                      <Chip
                        label={t('promoCodePanel.defaultPromoExpired')}
                        color="error"
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                        }}
                      />
                    </Box>
                  )}

                  {isPromoCodeNotActivated(entry) && (
                    <Box sx={{ mb: 2, mt: 4 }}>
                      <Chip
                        label={t('promoCodePanel.defaultPromoNotActivated')}
                        color="warning"
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                        }}
                      />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" mt={2}>
                      {t('promoCodePanel.programId')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {t(`EFinancingProgram.${entry.financingProgramId as EFinancingProgram}`, { ns: 'enum' })}
                    </Typography>

                    <Typography variant="subtitle2" color="text.secondary" mt={2}>
                      {t('promoCodePanel.plan')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {t(`EPlanType.${entry.planName}`, { ns: 'enum' })}
                    </Typography>

                    <Typography variant="subtitle2" color="text.secondary">
                      {t('promoCodePanel.defaultAppliedPromotionCode')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {FormatPromoCode(entry.defaultPromoCode)}
                    </Typography>

                    <Typography variant="subtitle2" color="text.secondary" mt={2}>
                      {entry.selectablePromoCodes.length === 0
                        ? t('promoCodePanel.noSelectablePromoCode')
                        : entry.selectablePromoCodes.length === 1
                          ? t('promoCodePanel.selectablePromoCodeSingular')
                          : t('promoCodePanel.selectablePromoCodesPlural')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {entry.selectablePromoCodes.length > 0 ? entry.selectablePromoCodes.join(', ') : '-'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
              <CardActions>
                <ExpandMore
                  expand={!!expandedCards[index]}
                  onClick={() => handleExpandClick(index)}
                  aria-expanded={!!expandedCards[index]}
                  aria-label="show more"
                >
                  <ExpandMoreIcon />
                </ExpandMore>
              </CardActions>
              <Collapse in={!!expandedCards[index]} timeout="auto" unmountOnExit>
                <CardContent>
                  <SelectedPromoPlanDisplay selectedPlan={getBestMatchingPlan(entry.defaultPromoCode)!} />
                </CardContent>
              </Collapse>
            </Card>
          </Grid>
        ))}
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderStyle: 'dashed',
            }}
            onClick={() => {
              setEntry(undefined)
              setOpenPlanPromoRuleDialog(true)
            }}
          >
            <IconButton>
              <Add fontSize="large" />
            </IconButton>
          </Card>
        </Grid>
      </Grid>
      <PlanPromoRuleDialog
        open={openPlanPromoRuleDialog}
        handleClose={() => setOpenPlanPromoRuleDialog(false)}
        availablePaymentPlans={availablePaymentPlans}
        control={control}
        supportFinancingPrograms={supportFinancingPrograms}
        setPromoCodeEntries={setPromoRules}
        entry={entry}
      />
    </Box>
  )
}

export default MerchantPromoPlanPanel
